// AppReview.js
import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, Rating, Button, Chip,
    ToggleButton, ToggleButtonGroup, Fade, Grow
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logo from "./assets/mealno-brand-logo.svg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { sendAppReview } from "./api/api";
import { fetchIP } from "./utils/fetchIP";

const T = {
    bg: "linear-gradient(135deg, #FFFBEA 0%, #FFF3C4 45%, #FFE9A8 100%)",
    card: "rgba(255,255,255,0.65)",
    glassBorder: "rgba(255,255,255,0.5)",
    gold: "#FFD700",
    goldDk: "#FFC107",
    brown: "#6D4C41",
    brownLt: "#A1887F",
    red: "#FF4500",
    border: "rgba(109,76,65,0.12)",
    shadow: "rgba(109,76,65,0.18)",
    gradBtn: "linear-gradient(135deg,#FFD700 0%,#FFC107 100%)",
};

const ISSUE_TAGS = [
    "App crashes / bugs", "Slow loading", "Confusing navigation",
    "Payment issues", "Food quality", "Wrong / missing items",
    "Late delivery", "Delivery partner behavior", "Pricing / value",
    "Customer support",
];

const RatingRow = ({ icon, label, value, onChange }) => (
    <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", borderRadius: "14px",
        background: "rgba(255,255,255,0.5)", border: `1px solid ${T.border}`,
    }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{
                width: 34, height: 34, borderRadius: "10px", background: T.gradBtn,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#4a342e",
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontWeight: 600, color: T.brown, fontSize: 14.5 }}>
                {label}
            </Typography>
        </Box>
        <Rating
            value={value}
            onChange={(e, v) => onChange(v)}
            size="small"
            sx={{ color: T.gold }}
        />
    </Box>
);

export default function AppReview() {
    const navigate = useNavigate();

    const [ratings, setRatings] = useState({
        overall: 0,
        usability: 0,
        food: 0,
        delivery: 0,
    });
    const [issues, setIssues] = useState([]);
    const [recommend, setRecommend] = useState(null);
    const [review, setReview] = useState({ name: "", contact: "", message: "" });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    // ✅ After a successful submission, redirect back to the site root.
    // In production, a full reload of "/review" 404s because the static host
    // has no SPA fallback rule for that path — only "/" is guaranteed to resolve.
    // useEffect(() => {
    //     if (submitted) {
    //         const timer = setTimeout(() => {
    //             // ─── PRODUCTION: redirect to domain root (safe, always resolves) ───
    //             // window.location.href = "https://www.mealno.in";

    //             // ─── LOCAL DEV: uncomment this and comment the line above ───
    //             window.location.reload();
    //         }, 3000); // 3 seconds

    //         return () => clearTimeout(timer);
    //     }
    // }, [submitted]);


    useEffect(() => {
        if (submitted) {
            const timer = setTimeout(() => {
                navigate("/");
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [submitted, navigate]);

    const toggleIssue = (tag) => {
        setIssues((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitting(true);

        try {
            const frontend_ip = await fetchIP();

            await sendAppReview({
                overall_rating: ratings.overall,
                usability_rating: ratings.usability || null,
                food_rating: ratings.food || null,
                delivery_rating: ratings.delivery || null,
                issues,
                recommend,
                name: review.name,
                contact: review.contact,
                message: review.message || null,
                frontend_ip,
            });

            setSubmitted(true);
        } catch (error) {
            console.error(error);
            setSubmitError(
                error?.response?.data?.detail ||
                "Unable to submit your review. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ background: T.bg, minHeight: "100vh", display: "flex", flexDirection: "column" }}>

            {/* Header */}
            <Box component="header" sx={{
                position: "fixed", top: 0, left: 0, right: 0, height: 64,
                background: "rgba(255,255,224,.75)", backdropFilter: "blur(14px)",
                borderBottom: `1px solid ${T.border}`, display: "flex",
                alignItems: "center", justifyContent: "flex-start",
                padding: { xs: "0 5%", md: "0 6%" },
                zIndex: 100,
            }}>
                <Box component="img" src={logo} alt="Mealno Logo"
                    sx={{
                        height: { xs: 36, md: 44 }, width: "auto", objectFit: "contain",
                    }} />
            </Box>

            {/* MAIN CONTENT */}
            <Box component="section" sx={{
                flex: 1, padding: "100px 5% 60px", display: "flex",
                justifyContent: "center", alignItems: "center",
            }}>
                <Grow in timeout={500}>
                    <Box sx={{
                        background: T.card, backdropFilter: "blur(20px) saturate(180%)",
                        border: `1px solid ${T.glassBorder}`, borderRadius: "24px",
                        padding: { xs: "32px 20px", md: "40px" }, maxWidth: "540px", width: "100%",
                        boxShadow: `0 12px 40px ${T.shadow}`, textAlign: "center",
                    }}>

                        {submitted ? (
                            <Fade in timeout={600}>
                                <Box>
                                    <Box sx={{
                                        fontSize: 60, marginBottom: "16px",
                                        animation: "pop .5s cubic-bezier(.34,1.56,.64,1) both",
                                        "@keyframes pop": {
                                            "0%": { transform: "scale(0)" },
                                            "100%": { transform: "scale(1)" },
                                        },
                                    }}>
                                        🎉
                                    </Box>
                                    <Typography component="h2" sx={{
                                        fontFamily: "'Playfair Display',serif", fontSize: 28,
                                        fontWeight: 900, color: "#4a342e", marginBottom: "8px",
                                    }}>
                                        Thank You!
                                    </Typography>
                                    <Typography sx={{ color: T.brownLt, fontSize: 15 }}>
                                        Your review helps us serve you better.
                                    </Typography>
                                </Box>
                            </Fade>
                        ) : (
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>

                                <Box>
                                    <Typography component="h2" sx={{
                                        fontFamily: "'Playfair Display',serif",
                                        fontSize: "clamp(1.7rem, 3vw, 2.3rem)",
                                        fontWeight: 900, color: "#4a342e", lineHeight: 1.1, mb: 0.5,
                                    }}>
                                        Rate Your Experience
                                    </Typography>
                                    <Typography sx={{ color: T.brownLt, fontSize: 14 }}>
                                        Tell us how Mealno is working for you overall
                                    </Typography>
                                </Box>

                                {/* Overall rating — big, front and center */}
                                <Box sx={{ margin: "4px 0" }}>
                                    <Rating
                                        value={ratings.overall}
                                        onChange={(e, v) => setRatings((r) => ({ ...r, overall: v }))}
                                        size="large"
                                        sx={{ color: T.gold, fontSize: "3rem" }}
                                    />
                                </Box>

                                {/* Category breakdown */}
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <RatingRow
                                        icon={<PhoneIphoneIcon fontSize="small" />}
                                        label="App usability"
                                        value={ratings.usability}
                                        onChange={(v) => setRatings((r) => ({ ...r, usability: v }))}
                                    />
                                    <RatingRow
                                        icon={<RestaurantIcon fontSize="small" />}
                                        label="Food quality"
                                        value={ratings.food}
                                        onChange={(v) => setRatings((r) => ({ ...r, food: v }))}
                                    />
                                    <RatingRow
                                        icon={<LocalShippingIcon fontSize="small" />}
                                        label="Delivery experience"
                                        value={ratings.delivery}
                                        onChange={(v) => setRatings((r) => ({ ...r, delivery: v }))}
                                    />
                                </Box>

                                {/* Issue tags */}
                                <Box sx={{ textAlign: "left" }}>
                                    <Typography sx={{ fontWeight: 700, color: T.brown, fontSize: 14, mb: 1 }}>
                                        What could be better? <span style={{ color: T.brownLt, fontWeight: 400 }}>(optional)</span>
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {ISSUE_TAGS.map((tag) => {
                                            const active = issues.includes(tag);
                                            return (
                                                <Chip
                                                    key={tag}
                                                    label={tag}
                                                    onClick={() => toggleIssue(tag)}
                                                    icon={active ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
                                                    sx={{
                                                        borderRadius: "10px", fontSize: 12.5, fontWeight: 600,
                                                        background: active ? T.gradBtn : "rgba(255,255,255,0.6)",
                                                        color: active ? "#4a342e" : T.brownLt,
                                                        border: `1px solid ${active ? "transparent" : T.border}`,
                                                        transition: "all .2s ease",
                                                        "&:hover": { background: active ? T.gradBtn : "rgba(255,255,255,0.9)" },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>

                                {/* Recommend toggle */}
                                <Box sx={{ textAlign: "left" }}>
                                    <Typography sx={{ fontWeight: 700, color: T.brown, fontSize: 14, mb: 1 }}>
                                        Would you recommend Mealno to a friend?
                                    </Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        value={recommend}
                                        onChange={(e, v) => v !== null && setRecommend(v)}
                                        sx={{ width: "100%", gap: "10px" }}
                                    >
                                        <ToggleButton
                                            value="yes"
                                            sx={{
                                                flex: 1, borderRadius: "12px !important", border: `1px solid ${T.border} !important`,
                                                textTransform: "none", fontWeight: 700, color: T.brown,
                                                "&.Mui-selected": { background: `${T.gradBtn} !important`, color: "#4a342e" },
                                            }}
                                        >
                                            🙂 Yes
                                        </ToggleButton>
                                        <ToggleButton
                                            value="no"
                                            sx={{
                                                flex: 1, borderRadius: "12px !important", border: `1px solid ${T.border} !important`,
                                                textTransform: "none", fontWeight: 700, color: T.brown,
                                                "&.Mui-selected": { background: "rgba(255,69,0,0.15) !important", color: T.red },
                                            }}
                                        >
                                            🙁 No
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Your Name"
                                    variant="outlined"
                                    value={review.name}
                                    onChange={(e) => setReview({ ...review, name: e.target.value })}
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: "14px", background: "rgba(255,255,255,0.5)",
                                        },
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Phone or Email"
                                    variant="outlined"
                                    value={review.contact}
                                    onChange={(e) => setReview({ ...review, contact: e.target.value })}
                                    placeholder="So we can follow up if needed"
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: "14px", background: "rgba(255,255,255,0.5)",
                                        },
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Anything else you'd like to share?"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    value={review.message}
                                    onChange={(e) => setReview({ ...review, message: e.target.value })}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: "14px", background: "rgba(255,255,255,0.5)",
                                        },
                                    }}
                                />

                                {submitError && (
                                    <Typography sx={{ color: T.red, fontSize: 13, fontWeight: 600, textAlign: "left" }}>
                                        {submitError}
                                    </Typography>
                                )}

                                <Box sx={{ display: "flex", gap: "12px", marginTop: "6px" }}>
                                    <Button
                                        onClick={() => navigate("/")}
                                        fullWidth
                                        disabled={submitting}
                                        sx={{
                                            border: `1px solid ${T.border}`, color: T.brown, fontWeight: 700,
                                            borderRadius: "14px", padding: "12px", textTransform: "none",
                                            background: "rgba(255,255,255,0.4)",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        disabled={!ratings.overall || submitting}
                                        sx={{
                                            background: ratings.overall ? T.gradBtn : "#e0e0e0",
                                            color: "#4a342e", fontWeight: 800, borderRadius: "14px",
                                            padding: "12px", textTransform: "none",
                                            boxShadow: ratings.overall ? `0 6px 16px ${T.shadow}` : "none",
                                            transition: "all .25s ease",
                                            opacity: submitting ? 0.7 : 1,
                                            "&:hover": {
                                                background: ratings.overall ? T.goldDk : "#e0e0e0",
                                                transform: ratings.overall ? "translateY(-2px)" : "none",
                                            },
                                        }}
                                    >
                                        {submitting ? "Submitting..." : "Submit Review"}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grow>
            </Box>
        </Box>
    );
}
